/**
 * useAiStream — generic React hook for consuming an AI SSE stream.
 *
 * Wraps an async-iterable "stream call" (e.g. `generateCoverLetterStream`)
 * with React state for incremental text, loading, error, and duration. The
 * hook is deliberately generic so it can be reused for future streaming AI
 * endpoints (interview prep, scoring commentary, etc.) as they are added.
 *
 * Cancellation is handled via an internal `AbortController` exposed through
 * the `cancel()` helper, which is also called implicitly when a new `trigger`
 * is fired or the component unmounts.
 *
 * Event contract (matches backend SSE payloads):
 *   - `{ type: 'chunk', text }`                   — append text
 *   - `{ type: 'done', duration_ms, ... }`        — final success marker
 *   - `{ type: 'error', error }`                  — terminal failure
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Shape of events yielded by the wrapped stream call. */
export interface AiStreamEvent {
  type: string;
  [key: string]: unknown;
}

export interface UseAiStreamResult<Req> {
  /** Accumulated text from all `chunk` events received so far. */
  text: string;
  /** `true` while the stream is open (from trigger to done/error/cancel). */
  isStreaming: boolean;
  /** Error message from a `{ type: 'error' }` event or thrown network error. */
  error: string | null;
  /** Total stream duration from the `done` event, in milliseconds. */
  durationMs: number | null;
  /** Start a new stream. Cancels any in-flight stream first. */
  trigger: (req: Req) => Promise<void>;
  /** Abort the in-flight stream (no-op if none is running). */
  cancel: () => void;
  /** Clear text/error/duration without cancelling any in-flight stream. */
  reset: () => void;
}

/**
 * Hook factory that binds a specific SSE stream endpoint to React state.
 *
 * @param streamCall - Async-iterable function that yields typed SSE events.
 *                     Must accept an AbortSignal as its second argument.
 */
export function useAiStream<Req>(
  streamCall: (req: Req, signal: AbortSignal) => AsyncIterable<AiStreamEvent>,
): UseAiStreamResult<Req> {
  const [text, setText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState<number | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback((): void => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback((): void => {
    setText('');
    setError(null);
    setDurationMs(null);
  }, []);

  // Auto-cancel on unmount to prevent state updates after unmount and to
  // free the server-side rate-limit slot.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const trigger = useCallback(
    async (req: Req): Promise<void> => {
      // Cancel any previous stream, reset state, start fresh.
      abortRef.current?.abort();
      setText('');
      setError(null);
      setDurationMs(null);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        for await (const event of streamCall(req, controller.signal)) {
          if (controller.signal.aborted) break;
          if (event.type === 'chunk' && typeof event['text'] === 'string') {
            const chunk = event['text'];
            setText((prev) => prev + chunk);
          } else if (event.type === 'done') {
            const dur = event['duration_ms'];
            if (typeof dur === 'number') {
              setDurationMs(dur);
            }
          } else if (event.type === 'error') {
            const msg = event['error'];
            setError(typeof msg === 'string' ? msg : 'Stream error');
          }
        }
      } catch (err: unknown) {
        // Only surface errors the user did not cause themselves via cancel().
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Stream failed');
        }
      } finally {
        setIsStreaming(false);
      }
    },
    [streamCall],
  );

  return { text, isStreaming, error, durationMs, trigger, cancel, reset };
}
